import type { NotificationView } from '#src/generated/protocol';

import {
  alertsNatively,
  nativeNotificationPermission,
  type NotificationGrant,
  requestNativeNotificationPermission,
} from '#lib/platform/native-notifications.js';
import { presentsInApp } from '#lib/platform/notifications.js';
import { preferences } from '#lib/settings/preferences.svelte.js';

export function enabled(): boolean {
  return presentsInApp() && preferences.systemNotifications && permission() === 'granted';
}

export function permission(): NotificationPermission {
  return presentsInApp() ? Notification.permission : 'denied';
}

export async function requestPermission(): Promise<NotificationPermission> {
  if (!presentsInApp()) return 'denied';
  return Notification.requestPermission();
}

export async function permissionGranted(): Promise<boolean> {
  if (presentsInApp()) return permission() === 'granted';
  return (await nativeNotificationPermission()) === 'granted';
}

export async function permissionState(): Promise<NotificationGrant | 'unsupported'> {
  if (presentsInApp()) {
    const current = permission();
    return current === 'default' ? 'prompt' : current;
  }
  if (!alertsNatively()) return 'unsupported';
  return nativeNotificationPermission();
}

const grantListeners = new Set<() => void>();

export function onPermissionGranted(listener: () => void): () => void {
  grantListeners.add(listener);
  return () => {
    grantListeners.delete(listener);
  };
}

export async function grantPermission(): Promise<boolean> {
  const granted = presentsInApp()
    ? (await requestPermission()) === 'granted'
    : (await requestNativeNotificationPermission()) === 'granted';
  if (granted) for (const listener of grantListeners) listener();
  return granted;
}

export function title(view: NotificationView): string {
  return view.room_name;
}

function showsContent(view: NotificationView): boolean {
  return (
    preferences.notificationContent && (!view.encrypted || preferences.notificationEncryptedContent)
  );
}

export function body(view: NotificationView): string {
  if (!showsContent(view)) {
    return view.is_direct ? 'New message' : `New message from ${sender(view)}`;
  }
  return view.is_direct ? view.body : `${sender(view)}: ${view.body}`;
}

function sender(view: NotificationView): string {
  return view.sender_name ?? view.sender;
}
